import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { getAttendeeWorkingHours as getAttendeeWorkingHoursV2 } from './getAttendeeWorkingHours';
import { getAttendeeFlexibleWorkingHours as getAttendeeFlexibleWorkingHoursV2 } from './getAttendeeFlexibleWorkingHours';
import {
    isBefore,
    isAfter,
    isSameDay,
    type OwaDate,
    owaDate,
    differenceInMinutes,
} from 'owa-datetime';

/**
 * Gets the timezone for an attendee
 *
 * We want to grab the timezone from WHv2 but if it does not exist, grab it from WHv1
 * Currently assumes that all flexible working hours have the same time zone given that we
 * do not have capability to set future working hours' time zones.
 * @param sessionId The free busy cache session
 * @param attendee The attendee for time zone is requested
 */
export function getAttendeeTimeZone(
    sessionId: string,
    attendee: AttendeeType,
    eventDate: OwaDate
): string | undefined {
    const flexibleWorkingHours = getAttendeeFlexibleWorkingHoursV2(sessionId, attendee);
    if (flexibleWorkingHours.length > 0) {
        // Try to find a FWH segment that contains the event's start date
        let eventDateTimeZone = flexibleWorkingHours.find(workingHours => {
            const workHoursDateRange = {
                start: owaDate(workingHours.start.timeZone.name, workingHours.start.dateTime),
                end: owaDate(workingHours.end.timeZone.name, workingHours.end.dateTime),
            };
            return (
                isAfter(eventDate, workHoursDateRange.start) &&
                isBefore(eventDate, workHoursDateRange.end)
            );
        });

        // If we weren't able to find a FWH segment that contains the event's start date,
        // get all the FWH segments that are on the same day as the event's start date
        if (!eventDateTimeZone) {
            const sameDaySegments = flexibleWorkingHours.filter(workingHours => {
                return isSameDay(
                    owaDate(workingHours.start.timeZone.name, workingHours.start.dateTime),
                    eventDate
                );
            });

            if (sameDaySegments && sameDaySegments.length > 0) {
                // If the event start is before the earliest segment start, use that segment's timezone
                if (
                    isBefore(
                        eventDate,
                        owaDate(
                            sameDaySegments[0].start.timeZone.name,
                            sameDaySegments[0].start.dateTime
                        )
                    )
                ) {
                    eventDateTimeZone = sameDaySegments[0];
                }
                // If the event start is after the latest segment end, use that segment's timezone
                else if (
                    isAfter(
                        eventDate,
                        owaDate(
                            sameDaySegments[sameDaySegments.length - 1].end.timeZone.name,
                            sameDaySegments[sameDaySegments.length - 1].end.dateTime
                        )
                    )
                ) {
                    eventDateTimeZone = sameDaySegments[sameDaySegments.length - 1];
                }
                // If the event start is between segments that are separated by a gap, find the closest segment start/end
                else {
                    let closestSegment = null;
                    let closestSegmentTimeDifference = Infinity;
                    /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
                     * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
                     *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
                    sameDaySegments.forEach(segment => {
                        const segmentDifference = Math.min(
                            differenceInMinutes(
                                eventDate,
                                owaDate(segment.start.timeZone.name, segment.start.dateTime)
                            ),
                            differenceInMinutes(
                                eventDate,
                                owaDate(segment.end.timeZone.name, segment.end.dateTime)
                            )
                        );
                        if (segmentDifference < closestSegmentTimeDifference) {
                            closestSegment = segment;
                            closestSegmentTimeDifference = segmentDifference;
                        }
                    });
                    if (closestSegment) {
                        eventDateTimeZone = closestSegment;
                    }
                }
            }
        }

        // If eventDateTimeZone was successfully set, return the time zone
        if (eventDateTimeZone) {
            return eventDateTimeZone.start.timeZone.name;
        }
    }
    // If the conditions above fall through, we will default to WHv1
    const workingHours = getAttendeeWorkingHoursV2(sessionId, attendee);
    return workingHours?.WorkingHoursTimeZoneId;
}
